/* eslint-disable react/require-default-props */
import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { Form, ListGroup, OverlayTrigger, Tooltip, Button } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { QUESTION_FILTER_TYPE, QUESTION_VALUE_TYPE } from '../../../../constants/common';
import { openModal } from '../../../../actions/modal.action';
import types from '../../../../types';

import benTooltip from '../../../../Components/BenTooltip';
import useOnboarding from '../../hooks/useOnboarding';

const FilterCategory = ({
  filterType,
  onFilterTypeChange,
  sample,
  steps,
  surveyLocationFilter,
}) => {
  const dispatch = useDispatch();
  const { isStepVisible, completeStep } = useOnboarding();

  const [show, setShow] = useState(false);
  const target = useRef(null);

  const handleClose = () => setShow(false);

  const handleLocationType = (event) => {
    const selectEvent = { ...event };
    if (surveyLocationFilter && surveyLocationFilter.id !== sample.Id) {
      openModal({
        className: 'modal-alert-width',
        button1Text: 'No',
        button1Action: 'closeModal',
        button2Text: 'Yes',
        button2Action: () => {
          onFilterTypeChange(selectEvent);
          dispatch({ type: types.reducerTypes.HIDE_MODAL });
        },
        imageSrc: '/icons/location.png',
        body:
          'You have already designated a question as the location filter for this survey. Do you want to use this question for the location filter instead?',
      })(dispatch);
    } else {
      onFilterTypeChange(selectEvent);
    }
  };

  const handleAttributeType = (event) => {
    openModal({
      className: 'modal-alert-width',
      button1Text: 'Okay',
      button1Action: 'closeModal',
      imageSrc: '/icons/triangle.png',
      body:
        'This question will not be shown as a chart, but will only be available as an attribute filter',
    })(dispatch);
    onFilterTypeChange(event);
  };

  const showStep4 = steps ? isStepVisible(steps.step4) : false;

  return (
    <>
      <div className="border-gray mt-3" />
      <h4 className="mb-3">
        Filter Category
        <OverlayTrigger
          target={target.current}
          show={show}
          onHide={handleClose}
          placement="top"
          overlay={
            <Tooltip>
              Select filter category to choose which type of filter this survey question will be, or
              select None to prevent your users from filtering by responses to the selected
              question.{' '}
              <a
                target="__blank"
                href="https://static1.squarespace.com/static/611337e8c220ef66fbc3f919/t/64a5d1fa0d77a55bfe7447b0/1688588806355/Survey+Explorer+Quick+Start+Guide"
              >
                Learn more about filters
              </a>
              <br />
              <br />
              <Button
                variant="outline-secondary"
                className="btn btn-link white mr-3 text-white"
                onClick={() => setShow(false)}
              >
                Close
              </Button>
            </Tooltip>
          }
          delay={300}
        >
          <FontAwesomeIcon
            ref={target}
            onMouseEnter={() => setShow(true)}
            className="cursor-pointer ml-2"
            size="1x"
            icon={faInfoCircle}
          />
        </OverlayTrigger>
      </h4>

      {steps && (
        <OverlayTrigger
          placement="right"
          defaultShow={showStep4}
          show={showStep4}
          overlay={(props) =>
            benTooltip({
              ...props,
              title: steps.step4.title,
              content: steps.step4.content,
              link: steps.step4.link,
              close: () => completeStep(steps.step4.value),
            })
          }
        >
          <ListGroup>
            <ListGroup.Item>
              <Form.Check
                type="radio"
                checked={filterType === QUESTION_FILTER_TYPE.NONE}
                id="filter-none"
                label="None"
                value="NONE"
                onChange={onFilterTypeChange}
              />
            </ListGroup.Item>
            {sample?.Data.valueType === QUESTION_VALUE_TYPE.SINGLE && (
              <ListGroup.Item>
                <Form.Check
                  checked={filterType === QUESTION_FILTER_TYPE.LOCATION}
                  type="radio"
                  id="filter-location"
                  label="Location"
                  value="LOCATION"
                  onChange={handleLocationType}
                />
              </ListGroup.Item>
            )}
            <ListGroup.Item>
              <Form.Check
                checked={filterType === QUESTION_FILTER_TYPE.ATTRIBUTE}
                type="radio"
                id="filter-attribute"
                label="Attribute"
                value="ATTRIBUTE"
                onChange={handleAttributeType}
              />
            </ListGroup.Item>
            <ListGroup.Item>
              <Form.Check
                checked={filterType === QUESTION_FILTER_TYPE.QUESTION}
                type="radio"
                id="filter-question"
                label="Question"
                value="QUESTION"
                onChange={onFilterTypeChange}
              />
            </ListGroup.Item>
          </ListGroup>
        </OverlayTrigger>
      )}
    </>
  );
};

FilterCategory.propTypes = {
  filterType: PropTypes.string.isRequired,
  onFilterTypeChange: PropTypes.func.isRequired,
  sample: PropTypes.instanceOf(Object).isRequired,
  steps: PropTypes.instanceOf(Object),
  surveyLocationFilter: PropTypes.instanceOf(Object),
};

export default FilterCategory;
